import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { checkApplicationStatus, getStatusMessage } from '../../services/api';
import { 
  MagnifyingGlassIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const ApplicationStatusPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [applicationId, setApplicationId] = useState('');
  const [email, setEmail] = useState('');
  const [application, setApplication] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formattedId, setFormattedId] = useState('');

  // Format application ID to standard format: CSEAN-APP-YYYY-XXX
  const formatApplicationId = (input: string): string => {
    if (!input) return '';
    
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    // Check if it starts with CSEAN
    if (!cleaned.startsWith('CSEAN')) {
      return input; // Return original if doesn't start with CSEAN
    }
    
    // Extract parts using regex
    const match = cleaned.match(/^CSEANAPP(\d{4})(\d+)$/);
    if (match) {
      const [, year, number] = match;
      return `CSEAN-APP-${year}-${number.padStart(3, '0')}`;
    }
    
    // Try alternative patterns
    const altMatch = cleaned.match(/^CSEANAPP(\d{2})(\d+)$/);
    if (altMatch) {
      const [, year, number] = altMatch;
      const fullYear = year.startsWith('20') ? `20${year}` : `20${year}`;
      return `CSEAN-APP-${fullYear}-${number.padStart(3, '0')}`;
    }
    
    // If already in correct format, return as is
    if (cleaned.match(/^CSEANAPP\d{4}\d{3}$/)) {
      const year = cleaned.substring(7, 11);
      const number = cleaned.substring(11);
      return `CSEAN-APP-${year}-${number}`;
    }
    
    return input; // Return original if can't format
  };

  // Handle pre-filled application ID from URL
  useEffect(() => {
    const applicationIdParam = searchParams.get('applicationId');
    const emailParam = searchParams.get('email');
    if (applicationIdParam) {
      setApplicationId(applicationIdParam);
      if (emailParam) setEmail(emailParam);
      // A link that carries both can answer straight away; with only the ID, the form
      // waits for the email address, which is what keeps the lookup private.
      if (emailParam) handleCheckStatusWithId(applicationIdParam, emailParam);
    }
  }, [searchParams]);

  const handleCheckStatusWithId = async (id: string, emailAddress: string) => {
    setLoading(true);
    setError('');

    try {
      const result = await checkApplicationStatus(id, emailAddress);
      setApplication(result.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setApplicationId(value);
    const formatted = formatApplicationId(value);
    setFormattedId(formatted);
  };

  const handleCheckStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    const idToUse = formattedId || applicationId;
    setLoading(true);
    setError('');

    try {
      const result = await checkApplicationStatus(idToUse, email.trim());
      setApplication(result.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending_review':
        return <ClockIcon className="w-8 h-8 text-yellow-500" />;
      case 'approved':
        return <CheckCircleIcon className="w-8 h-8 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="w-8 h-8 text-red-500" />;
      case 'pending_payment':
        return <ExclamationTriangleIcon className="w-8 h-8 text-orange-500" />;
      case 'completed':
        return <CheckCircleIcon className="w-8 h-8 text-green-600" />;
      default:
        return <ClockIcon className="w-8 h-8 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending_payment':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-max py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Check Application Status
            </h1>
            <p className="text-lg text-gray-600">
              Enter your application ID and the email you applied with to check the status of
              your membership application
            </p>
          </div>
        </div>
      </div>

      {/* Status Check Form */}
      <div className="section-padding">
        <div className="container-max max-w-2xl mx-auto">
          <div className="card">
            <form onSubmit={handleCheckStatus} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Application ID
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={applicationId}
                    onChange={(e) => handleInputChange(e.target.value)}
                    placeholder="Enter your application ID"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  You can find your application ID in the confirmation email we sent you.
                </p>
                {formattedId && formattedId !== applicationId && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Formatted ID:</strong> <span className="font-mono">{formattedId}</span>
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      We've automatically formatted your input for the correct format.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="The email you applied with"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  We ask for this so that only you can see your application.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !applicationId.trim() || !email.trim()}
                className="w-full btn-primary py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Checking Status...
                  </div>
                ) : (
                  'Check Status'
                )}
              </button>
            </form>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <XCircleIcon className="w-5 h-5 text-red-400 mr-3 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {application && (
              <div className="mt-8 space-y-6">
                {/* Status Card */}
                <div className={`p-6 rounded-lg border-2 ${getStatusColor(application.status)}`}>
                  <div className="flex items-center mb-4">
                    {getStatusIcon(application.status)}
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Application Status</h3>
                      <p className="text-sm opacity-75">
                        {getStatusMessage(application.status)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Application Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Details</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <UserIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Name</p>
                        <p className="font-medium">{application.firstName} {application.lastName}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{application.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Application ID</p>
                        <p className="font-medium font-mono">{application.applicationId}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <CurrencyDollarIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Application Fee</p>
                        <p className="font-medium">₦{application.applicationFee?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {application.membershipNumber && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Membership Number</p>
                          <p className="font-medium font-mono text-green-600">{application.membershipNumber}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {application.reviewedAt && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center">
                        <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Reviewed On</p>
                          <p className="font-medium">{new Date(application.reviewedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {application.reviewComments && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-2">Review Comments</p>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{application.reviewComments}</p>
                    </div>
                  )}
                </div>

                {/* Next Steps */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-primary-900 mb-2">Next Steps</h3>
                  {application.status === 'pending_review' && (
                    <p className="text-primary-700">
                      Your application is being reviewed by our team. You will receive an email notification 
                      once the review is complete. This process typically takes 2-3 business days.
                    </p>
                  )}
                  {application.status === 'approved' && (
                    <p className="text-primary-700">
                      Congratulations! Your application has been approved. Please check your email for 
                      login credentials to access the member portal.
                    </p>
                  )}
                  {application.status === 'pending_payment' && (
                    <p className="text-primary-700">
                      Your application has been approved. Please log in to the member portal to complete 
                      your payment and activate your membership.
                    </p>
                  )}
                  {application.status === 'completed' && (
                    <p className="text-primary-700">
                      Welcome to CSEAN! Your membership is now active. You can access all member benefits 
                      through the member portal.
                    </p>
                  )}
                  {application.status === 'rejected' && (
                    <p className="text-primary-700">
                      We're sorry, but your application was not approved. Please check your email for 
                      detailed feedback and consider reapplying in the future.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatusPage;
